import React, { useState, useEffect, useCallback } from "react";
import Loading from "../../../common/components/loading/loading";
import { getQualityAttributeValueMarketAssignments } from "../../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import MarketQualityAttributeAssignmentsTable from "./marketQualityAttributeAssignmentsTable";
import MarketQualityAttributeAssignmentInput from "./marketQualityAttributeAssignmentInput";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";

const MarketQualityAttributeAssignments = ({
  allowEdit,
  marketId,
  productId,
}) => {
  const { t } = useTranslation(["markets"]);
  const [assignments, setAssignments] = useState([]);
  const [assignment, setAssignment] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNew, setIsNew] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const loadAssignments = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadedAssignments = await getQualityAttributeValueMarketAssignments(
        marketId
      );
      setAssignments(loadedAssignments || []);
    } catch (error) {
      console.error("Error loading assignments:", error);
      setAssignments([]);
    } finally {
      setIsLoading(false);
    }
  }, [marketId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleUpdate = useCallback((updatedAssignment) => {
    setAssignments((prevAssignments) => {
      const assignments = [...prevAssignments];
      const i = assignments.findIndex(
        (_assignment) =>
          _assignment.marketId === updatedAssignment.marketId &&
          _assignment.qualityAttributeValueId ===
            updatedAssignment.qualityAttributeValueId
      );
      if (i > -1) assignments[i] = updatedAssignment;
      else assignments.push(updatedAssignment);
      return assignments;
    });
    setShowModal(false);
  }, []);

  const handleBatchUpdate = useCallback((newAssignments) => {
    // Merge new assignments with existing ones
    setAssignments((prevAssignments) => {
      const assignments = [...prevAssignments];

      newAssignments.forEach((newAssignment) => {
        const i = assignments.findIndex(
          (_assignment) =>
            _assignment.marketId === newAssignment.marketId &&
            _assignment.qualityAttributeValueId ===
              newAssignment.qualityAttributeValueId
        );
        if (i > -1) {
          assignments[i] = newAssignment;
        } else {
          assignments.push(newAssignment);
        }
      });

      return assignments;
    });
    setShowModal(false);
  }, []);

  const handleAssignmentSelected = useCallback(
    (selectedAssignment, isNewAssignment = false) => {
      setAssignment(selectedAssignment);
      setIsNew(isNewAssignment);
      setShowModal(true);
    },
    []
  );

  const handleAssignmentUpdated = useCallback(async () => {
    // Refresh the assignments list after update
    await loadAssignments();
  }, [loadAssignments]);

  const handleToggleActiveOnly = useCallback(() => {
    setShowActiveOnly((prev) => !prev);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-3 flex-column d-flex">
          {allowEdit && (
            <button
              onClick={() =>
                handleAssignmentSelected({ marketId: marketId }, true)
              }
              className="btn btn-sm btn-primary float-sm-right"
              style={{ marginTop: "auto" }}
            >
              {t("AddAssignment")}
            </button>
          )}
        </div>
        <div className="col-md-9 d-flex justify-content-end align-items-center">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={showActiveOnly}
            value={showActiveOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
      </div>
      <div className="row">
        <Modal
          show={showModal}
          onHide={handleClose}
          backdrop="static"
          keyboard={false}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isNew ? t("AddAssignment") : t("UpdateAssignment")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MarketQualityAttributeAssignmentInput
              marketId={marketId}
              productId={productId}
              assignment={assignment}
              allowEdit={allowEdit}
              isNew={isNew}
              submitCallbackFunction={isNew ? handleBatchUpdate : handleUpdate}
              onClose={handleClose}
            />
          </Modal.Body>
        </Modal>
        <div className="col">
          <MarketQualityAttributeAssignmentsTable
            assignments={assignments}
            marketId={marketId}
            allowEdit={allowEdit}
            onAssignmentUpdated={handleAssignmentUpdated}
            showActiveOnly={showActiveOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default MarketQualityAttributeAssignments;
